/**
 * RoutePinsLayer Component
 *
 * Renders a collection of route pins on the gym floor plan map.
 * Handles coordinate denormalization from normalized (0-1) to pixel coordinates.
 *
 * Features:
 * - Renders pins for routes with mapPosition
 * - Shows completion status (sent/unsent) per route
 * - Handles pin click/tap for route selection
 * - Filters out routes without map positions
 *
 * @example
 * ```tsx
 * <RoutePinsLayer
 *   routes={routes}
 *   sentRouteIds={sentRouteIds}
 *   colorGrades={gym.colorGrades}
 *   imageWidth={1920}
 *   imageHeight={1080}
 *   selectedRouteId={selectedRouteId}
 *   onRouteClick={(route) => setSelectedRoute(route)}
 * />
 * ```
 */

import { useMemo, useCallback } from 'react';
import { Group } from 'react-konva';
import type Konva from 'konva';
import { RoutePin } from './RoutePin';
import { isVGrade } from '@flash/shared';
import type { Route, ColorGrade, PrimaryIdentificationMethod } from '@flash/shared';

/**
 * Props for RoutePinsLayer component
 */
export interface RoutePinsLayerProps {
  /** Routes to render pins for */
  routes: Route[];
  /** Set of route IDs that the user has sent */
  sentRouteIds: Set<string>;
  /** Gym's color grades for looking up hold colors (or all colors when linked) */
  colorGrades: ColorGrade[];
  /** Gym's tag color grades (only used when colors are unlinked) */
  tagColorGrades?: ColorGrade[] | undefined;
  /** Primary identification method from gym settings */
  primaryMethod?: PrimaryIdentificationMethod | undefined;
  /** Floor plan image width in pixels */
  imageWidth: number;
  /** Floor plan image height in pixels */
  imageHeight: number;
  /** Currently selected route ID (optional) */
  selectedRouteId?: string;
  /** Called when a route pin is clicked/tapped */
  onRouteClick?: (route: Route) => void;
  /** Pin size in screen pixels at base scale (default: 28) */
  pinSize?: number;
  /** Whether the layer is visible (default: true) */
  visible?: boolean;
  /** Current viewport scale (for inverse scaling) */
  viewportScale?: number;
  /** Initial scale when map first loaded (for inverse scaling reference) */
  initialScale?: number;
}

/**
 * Default pin size in screen pixels at initial scale
 * This is the target screen size - inverse scaling keeps pins this size regardless of zoom
 */
const DEFAULT_PIN_SIZE = 32;

/**
 * Fallback color when colorGrade is not found
 */
const FALLBACK_COLOR = '#6B7280';

/**
 * Minimum distance between pin centers (as a fraction of pin size)
 * Pins closer than this will be spread apart - lower value allows more overlap
 */
const MIN_SEPARATION_FACTOR = 0.4;

/**
 * Maximum distance a pin can drift from its original position (as a fraction of pin size)
 * Lower value keeps pins closer to where the setter placed them
 */
const MAX_DRIFT_FACTOR = 0.5;

/**
 * Interface for a positioned pin with route reference
 */
interface PositionedPin {
  routeId: string;
  originalX: number;
  originalY: number;
  adjustedX: number;
  adjustedY: number;
}

/**
 * Spread overlapping pins just enough to be distinguishable.
 * Uses iterative collision resolution with strong pull-back to original position.
 */
const spreadOverlappingPins = (
  pins: PositionedPin[],
  minSeparation: number,
  maxDrift: number,
  maxIterations: number = 20
): PositionedPin[] => {
  if (pins.length <= 1) return pins;

  // Create a working copy
  const result = pins.map((p) => ({ ...p }));

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    let hadCollision = false;

    for (let i = 0; i < result.length; i++) {
      for (let j = i + 1; j < result.length; j++) {
        const pinA = result[i]!;
        const pinB = result[j]!;

        const dx = pinB.adjustedX - pinA.adjustedX;
        const dy = pinB.adjustedY - pinA.adjustedY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < minSeparation && distance > 0.001) {
          hadCollision = true;

          // Gentle push - only move enough to barely separate
          const overlap = minSeparation - distance;
          const pushX = (dx / distance) * overlap * 0.3;
          const pushY = (dy / distance) * overlap * 0.3;

          // Push pins apart gently
          pinA.adjustedX -= pushX;
          pinA.adjustedY -= pushY;
          pinB.adjustedX += pushX;
          pinB.adjustedY += pushY;
        } else if (distance <= 0.001) {
          // Pins at exact same position - spread in a small circle
          hadCollision = true;
          const angle = (j / result.length) * Math.PI * 2;
          pinB.adjustedX = pinA.adjustedX + Math.cos(angle) * minSeparation * 0.4;
          pinB.adjustedY = pinA.adjustedY + Math.sin(angle) * minSeparation * 0.4;
        }
      }
    }

    // Strong pull back toward original position
    for (const pin of result) {
      const pullStrength = 0.3;
      pin.adjustedX += (pin.originalX - pin.adjustedX) * pullStrength;
      pin.adjustedY += (pin.originalY - pin.adjustedY) * pullStrength;

      // Enforce maximum drift limit
      const driftX = pin.adjustedX - pin.originalX;
      const driftY = pin.adjustedY - pin.originalY;
      const driftDistance = Math.sqrt(driftX * driftX + driftY * driftY);

      if (driftDistance > maxDrift) {
        const scale = maxDrift / driftDistance;
        pin.adjustedX = pin.originalX + driftX * scale;
        pin.adjustedY = pin.originalY + driftY * scale;
      }
    }

    if (!hadCollision) break;
  }

  return result;
};

/**
 * Get the hex color for a color name from gym's color grades
 */
const getColorHex = (colorName: string | undefined, colorGrades: ColorGrade[]): string | null => {
  if (!colorName) return null;
  const colorGrade = colorGrades.find((cg) => cg.displayName === colorName);
  return colorGrade?.color ?? null;
};

/**
 * RoutePinsLayer Component
 *
 * Renders route pins on the map for routes that have mapPosition set.
 * Handles click events and shows completion status.
 */
export const RoutePinsLayer = ({
  routes,
  sentRouteIds,
  colorGrades,
  tagColorGrades,
  primaryMethod: _primaryMethod = 'holdColors',
  imageWidth,
  imageHeight,
  selectedRouteId,
  onRouteClick,
  pinSize = DEFAULT_PIN_SIZE,
  visible = true,
  viewportScale,
  initialScale,
}: RoutePinsLayerProps) => {
  // Determine which color grades to use for tag lookups
  // If tagColorGrades is provided (unlinked mode), use it; otherwise use colorGrades
  const effectiveTagColorGrades = tagColorGrades ?? colorGrades;

  // Filter routes that have map positions
  const routesWithPosition = useMemo(
    () => routes.filter((route) => route.mapPosition !== null),
    [routes]
  );

  // Calculate inverse-scaled pin size to maintain consistent screen size
  // When viewport zooms to 3x, pins would appear 3x larger without compensation
  // We scale them down inversely so they stay the same screen size
  const effectivePinSize = useMemo(() => {
    if (!viewportScale || !initialScale || initialScale === 0) {
      return pinSize;
    }
    // Scale factor relative to initial view
    const relativeScale = viewportScale / initialScale;
    // Inverse scale the pin size, with reasonable bounds
    const inversePinSize = pinSize / relativeScale;
    // Clamp to reasonable range:
    // - Minimum: 60% of base size so pins stay tappable when zoomed way in
    // - Maximum: 2x base size so pins don't get huge when zoomed out
    const minSize = pinSize * 0.6;
    return Math.max(minSize, Math.min(pinSize * 2, inversePinSize));
  }, [pinSize, viewportScale, initialScale]);

  // Calculate spread positions for overlapping pins using FIXED separation
  // This only recalculates when routes change, NOT on every zoom/pan
  const spreadPins = useMemo(() => {
    // Use fixed base pin size for separation calculation (not zoom-dependent)
    const baseSeparation = pinSize * MIN_SEPARATION_FACTOR;
    const baseMaxDrift = pinSize * MAX_DRIFT_FACTOR;

    // Create initial pin positions
    const pins: PositionedPin[] = routesWithPosition.map((route) => {
      const position = route.mapPosition!;
      const pixelX = position.x * imageWidth;
      const pixelY = position.y * imageHeight;
      return {
        routeId: route.id,
        originalX: pixelX,
        originalY: pixelY,
        adjustedX: pixelX,
        adjustedY: pixelY,
      };
    });

    // Spread overlapping pins with gentle separation
    return spreadOverlappingPins(pins, baseSeparation, baseMaxDrift);
  }, [routesWithPosition, imageWidth, imageHeight, pinSize]);

  // Create a map for quick lookup of adjusted positions
  const positionMap = useMemo(() => {
    const map = new Map<string, { x: number; y: number }>();
    for (const pin of spreadPins) {
      map.set(pin.routeId, { x: pin.adjustedX, y: pin.adjustedY });
    }
    return map;
  }, [spreadPins]);

  // Handle route click
  const handleRouteClick = useCallback(
    (route: Route) => (_e: Konva.KonvaEventObject<MouseEvent>) => {
      onRouteClick?.(route);
    },
    [onRouteClick]
  );

  // Handle route tap
  const handleRouteTap = useCallback(
    (route: Route) => (_e: Konva.KonvaEventObject<TouchEvent>) => {
      onRouteClick?.(route);
    },
    [onRouteClick]
  );

  // Don't render if not visible
  if (!visible) {
    return null;
  }

  // Don't render if no routes have positions
  if (routesWithPosition.length === 0) {
    return null;
  }

  return (
    <Group>
      {routesWithPosition.map((route) => {
        // Get the spread-adjusted position
        const adjustedPos = positionMap.get(route.id);
        if (!adjustedPos) return null;

        const isSent = sentRouteIds.has(route.id);
        const isSelected = selectedRouteId === route.id;

        // Get hold and tag colors from identification
        // Hold colors use colorGrades, tag colors use effectiveTagColorGrades
        const holdColorHex = getColorHex(route.identification?.holdColor, colorGrades);
        const tagColorHex = getColorHex(route.identification?.tagColor, effectiveTagColorGrades);

        // Resolve pin colors:
        // holdColor = explicit hold color, or legacy colorGrade lookup, or fallback gray
        const pinHoldColor =
          holdColorHex ?? getColorHex(route.colorGrade, colorGrades) ?? FALLBACK_COLOR;
        const pinTagColor = tagColorHex ?? undefined;

        // Only show V/Font grades on the map, not color grade names
        const grade = isVGrade(route.officialGrade) ? route.officialGrade : '';

        // Build RoutePin props with adjusted position and size
        // Selected pins are 40% larger to make them stand out
        const pinDisplaySize = isSelected ? effectivePinSize * 1.4 : effectivePinSize;
        const pinProps = {
          id: `pin-${route.id}`,
          x: adjustedPos.x,
          y: adjustedPos.y,
          grade,
          holdColor: pinHoldColor,
          ...(pinTagColor ? { tagColor: pinTagColor } : {}),
          isSent,
          isSelected,
          size: pinDisplaySize,
          onClick: handleRouteClick(route),
          onTap: handleRouteTap(route),
        };

        return <RoutePin key={route.id} {...pinProps} />;
      })}
    </Group>
  );
};

export default RoutePinsLayer;
